import {LoadWrapper} from 'components/LoadWrapper';
import {ViewAuthorizer} from 'components/ViewAuthorizer';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {GlobalAnnouncement, UserRole} from 'types';
import {
	GlobalAnnouncementsTranslationProvider,
	ProviderThatEnablesGettingTooltipsFromContext,
} from 'features/localizedTooltips';
import {EntityPage} from 'components';
import {
	renderArrayField,
	renderDateString,
	renderPersona,
} from 'components/EntityList/ColumnRenderers';
import {IColumn} from '@fluentui/react/lib/components/DetailsList';
import {Control} from 'react-hook-form';
import {
	GetAllGlobalAnnouncementsDocument,
	useCreateGlobalAnnouncementMutation,
	useDeleteGlobalAnnouncementMutation,
	useGetAllGlobalAnnouncementsQuery,
	useUpdateGlobalAnnouncementMutation,
} from './hooks/globalAnnouncements.generated';
import {
	ControlledCheckbox,
	ControlledDatePicker,
	ControlledTagPicker,
} from 'components/hookForms';
import {ControlledRichtextEditor} from 'components/hookForms/ControlledRichtextEditor';
import {PanelType, PersonaSize} from '@fluentui/react';
import {GlobalAnnouncementState} from 'features/GlobalAnnouncements/GlobalAnnouncementPopup';
import {useStore} from 'react-context-hook';
import {LoadSpinner} from 'components/LoadWrapper/LoadSpinner';

const AdminGlobalAnnouncementPage: React.FC = () => {
	const {t} = useTranslation('features/adminsection', {
		keyPrefix: 'AdminGlobalAnnouncements',
	});
	const [state, setState] = useStore<GlobalAnnouncementState>(
		'globalAnnouncementPopup',
	);
	const [announcementsSorted, setAnnouncementsSorted] = useState<
		GlobalAnnouncement[]
	>([]);

	const {data, loading} = useGetAllGlobalAnnouncementsQuery();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [updateGlobalAnnouncementMutation] =
		useUpdateGlobalAnnouncementMutation();
	const [createGlobalAnnouncementMutation] =
		useCreateGlobalAnnouncementMutation();
	const [deleteGlobalAnnouncementMutation] =
		useDeleteGlobalAnnouncementMutation();
	const refetchQueries = [GetAllGlobalAnnouncementsDocument];

	useEffect(() => {
		if (data?.allGlobalAnnouncements) {
			setAnnouncementsSorted(
				[...data.allGlobalAnnouncements].sort((a, b) =>
					a.createdAt < b.createdAt ? 1 : -1,
				),
			);
		}
	}, [data]);

	const columns: IColumn[] = [
		{
			key: 'content',
			name: t('Content'),
			fieldName: 'content',
			minWidth: 400,
			isResizable: true,
			onRender(item: any) {
				return <div dangerouslySetInnerHTML={{__html: item.content}} />;
			},
		},
		{
			key: 'roles',
			name: t('Roles'),
			fieldName: 'roles',
			minWidth: 200,
			isResizable: true,
			onRender: renderArrayField(),
		},
		{
			key: 'activated',
			name: t('Activated'),
			fieldName: 'activated',
			minWidth: 100,
			isResizable: true,
		},
		{
			key: 'emailNotification',
			name: t('EmailNotification'),
			fieldName: 'emailNotification',
			minWidth: 100,
			isResizable: true,
		},
		{
			key: 'startDate',
			name: t('StartDate'),
			fieldName: 'startDate',
			minWidth: 100,
			isResizable: true,
			onRender: renderDateString(),
		},
		{
			key: 'endDate',
			name: t('EndDate'),
			fieldName: 'endDate',
			minWidth: 100,
			isResizable: true,
			onRender: renderDateString(),
		},
		{
			key: 'createdBy',
			name: t('CreatedBy'),
			fieldName: 'createdBy',
			minWidth: 150,
			isResizable: true,
			onRender: renderPersona({size: PersonaSize.size8}),
		},
		{
			key: 'createdAt',
			name: t('CreatedAt'),
			fieldName: 'createdAt',
			minWidth: 100,
			isResizable: true,
			onRender: renderDateString(),
		},
	];

	const mapRoles = (entity: GlobalAnnouncement) =>
		entity.roles?.map(role => {
			if (typeof role === 'string') {
				return role;
			}
			return (role as any)?.name;
		}) || [];

	const createGlobalAnnouncement = async (newEntity: GlobalAnnouncement) => {
		if (isSubmitting) {
			console.error(`An operation is already ongoing!`);
			return;
		}
		setIsSubmitting(true);
		await createGlobalAnnouncementMutation({
			variables: {
				input: {
					name: newEntity.name,
					content: newEntity.content,
					activated: newEntity.activated,
					emailNotification: newEntity.emailNotification,
					roles: mapRoles(newEntity), // all ? ['ALL'] : mapRoles(newEntity),
					endDate: newEntity.endDate,
					startDate: newEntity.startDate,
				},
			},
			refetchQueries,
		}).finally(() => setIsSubmitting(false));
		setState({
			...state,
			refetch: true,
		});
	};

	const updateGlobalAnnouncement = async (
		updatedEntity: GlobalAnnouncement,
	) => {
		if (isSubmitting) {
			console.error(`An operation is already ongoing!`);
			return;
		}
		setIsSubmitting(true);
		await updateGlobalAnnouncementMutation({
			variables: {
				input: {
					id: updatedEntity.id,
					name: updatedEntity.name,
					content: updatedEntity.content,
					activated: updatedEntity.activated,
					emailNotification: updatedEntity.emailNotification,
					roles: mapRoles(updatedEntity), // all ? ['ALL'] : mapRoles(updatedEntity),
					endDate: updatedEntity.endDate,
					startDate: updatedEntity.startDate,
				},
			},
			refetchQueries,
		}).finally(() => setIsSubmitting(false));
		setState({
			...state,
			refetch: true,
		});
	};

	const deleteGlobalAnnouncement = (entityId: string) => {
		deleteGlobalAnnouncementMutation({
			variables: {
				input: {
					id: entityId,
				},
			},
			refetchQueries,
		});
	};

	const [startDate, setStartDate] = useState<Date | undefined>(undefined);
	const [endDate, setEndDate] = useState<Date | undefined>(undefined);
	const [datePickerKey, setDatePickerKey] = useState<string>('initialKey');
	const [all, setAll] = useState<boolean>(false);

	useEffect(() => {
		setDatePickerKey(`${startDate?.getDate()}-${endDate?.getDate()}`);
	}, [startDate, endDate]);
	const checkboxStyles = {root: {paddingLeft: '5px', paddingTop: '5px'}};
	const formElements = (control: Control<GlobalAnnouncement>) => {
		return (
			<div>
				<ProviderThatEnablesGettingTooltipsFromContext>
					<ControlledRichtextEditor
						control={control}
						label={t('Content')}
						name={'content'}
						rules={{required: t('ContentRequired')}}
					/>
					<ControlledTagPicker
						name={'roles'}
						control={control}
						disabled={all}
						selectableItems={[
							'ALL',
							UserRole.Vex,
							UserRole.VexSupport,
							UserRole.Vko,
							UserRole.Readers,
							UserRole.RegulationReader,
							UserRole.Avk,
							UserRole.Bedarfskoordinator,
							UserRole.ShApprover,
							UserRole.SystemAdministrator,
						].map(el => {
							return {
								id: el,
								name: el,
							};
						})}
						label={t('Roles')}
						hideTagIcon
						placeholder=''
						getKey={item => (item as any)?.id}
						getName={item =>
							typeof item === 'string' ? item : (item as any)?.name
						}
						rules={{required: !all}}
					/>
					<ControlledCheckbox
						name='activated'
						label={t('Activated')}
						control={control}
						styles={checkboxStyles}
					/>
					<ControlledCheckbox
						name='emailNotification'
						label={t('EmailNotification')}
						control={control}
						styles={checkboxStyles}
					/>
					<ControlledDatePicker
						key={'startDate' + datePickerKey}
						control={control}
						name={'startDate'}
						label={t('StartDate')}
						onChange={(date: any) => setStartDate(date)}
						rules={{
							validate(date) {
								if (endDate && endDate < date) return t('StartAfterEndDate');
								return true;
							},
						}}
					/>
					<ControlledDatePicker
						key={'endDate' + datePickerKey}
						control={control}
						name={'endDate'}
						label={t('EndDate')}
						onChange={(date: any) => setEndDate(date)}
						rules={{
							validate(date) {
								if (startDate && date < startDate)
									return t('StartAfterEndDate');
								return true;
							},
						}}
					/>
					{isSubmitting && (
						<LoadSpinner
							text={t('PleaseWait')}
							extraStyles={{root: {height: '120px', top: '100px'}}}
						/>
					)}
				</ProviderThatEnablesGettingTooltipsFromContext>
			</div>
		);
	};

	return (
		<ViewAuthorizer roles={[UserRole.SystemAdministrator]}>
			<LoadWrapper loading={loading}>
				<GlobalAnnouncementsTranslationProvider>
					<EntityPage
						items={announcementsSorted}
						deleteEntity={deleteGlobalAnnouncement}
						entityDisplayName={''}
						createEntity={createGlobalAnnouncement}
						updateEntity={updateGlobalAnnouncement}
						additionalColumns={columns}
						panelType={PanelType.large}
						renderAdditionalFormElements={formElements}
						customTranslations={{
							createHeaderTranslationKey: 'CreateGlobalAnnouncement',
							editHeaderTranslationKey: 'EditGlobalAnnouncement',
						}}
					/>
				</GlobalAnnouncementsTranslationProvider>
			</LoadWrapper>
		</ViewAuthorizer>
	);
};

export default AdminGlobalAnnouncementPage;
